import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FileText, Download, TrendUp, Calendar, WhatsappLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const [summary, setSummary] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openWhatsApp, setOpenWhatsApp] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, monthlyRes, categoryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stats/summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/stats/monthly`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/stats/categories`, { withCredentials: true })
      ]);
      setSummary(summaryRes.data);
      setMonthlyStats(monthlyRes.data);
      setCategoryStats(categoryRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    toast.info('Funcionalidade de exportação em desenvolvimento');
  };

  const handleSendWhatsApp = async (e) => {
    e.preventDefault();
    setSendingWhatsApp(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/reports/whatsapp`, {
        phone_number: phoneNumber
      }, { withCredentials: true });
      
      toast.success('Relatório enviado com sucesso!');
      setOpenWhatsApp(false);
      setPhoneNumber('');
      
      // Show the message that would be sent
      if (response.data.report_text) {
        console.log('WhatsApp Message:', response.data.report_text);
      }
    } catch (error) {
      toast.error('Erro ao enviar relatório');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) return <LoadingBar text="Carregando relatórios..." />;

  return (
    <div className="p-3 sm:p-6 md:p-8 space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-1 sm:mb-2">Relatórios</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Análises detalhadas das suas finanças</p>
        </div>
        <div className="flex space-x-2">
          <Dialog open={openWhatsApp} onOpenChange={setOpenWhatsApp}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="whatsapp-button">
                <WhatsappLogo size={20} className="mr-2" weight="fill" />
                Enviar via WhatsApp
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Enviar Relatório via WhatsApp</DialogTitle>
                <DialogDescription>
                  Receba seu relatório financeiro diretamente no WhatsApp
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSendWhatsApp} className="space-y-4">
                <div>
                  <Label htmlFor="phone">Número do WhatsApp</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    data-testid="phone-input"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Formato: DDD + número com 9 dígitos
                  </p>
                </div>
                <Button type="submit" className="w-full" disabled={sendingWhatsApp} data-testid="send-whatsapp">
                  {sendingWhatsApp ? 'Enviando...' : 'Enviar Relatório'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          
          <Button onClick={handleExportPDF} data-testid="export-pdf-button">
            <Download size={20} className="mr-2" />
            Exportar PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Receita Total</p>
              <p className="text-2xl font-bold text-green-600 tabular-nums">R$ {(summary?.total_income || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Despesa Total</p>
              <p className="text-2xl font-bold text-red-600 tabular-nums">R$ {(summary?.total_expenses || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Saldo Líquido</p>
              <p className={`text-2xl font-bold tabular-nums ${summary?.net_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>R$ {(summary?.net_balance || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Total Investido</p>
              <p className="text-2xl font-bold text-blue-600 tabular-nums">R$ {(summary?.total_invested || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="monthly" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="monthly" data-testid="tab-monthly">Mensal</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categorias</TabsTrigger>
          <TabsTrigger value="comparison" data-testid="tab-comparison">Comparação</TabsTrigger>
        </TabsList>
        
        <TabsContent value="monthly" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Análise Mensal</CardTitle>
              <CardDescription>Receitas e despesas por mês</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={monthlyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
                  <Legend />
                  <Bar dataKey="income" name="Receitas" fill="#10b981" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="expense" name="Despesas" fill="#ef4444" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="categories" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Distribuição por Categoria</CardTitle>
                <CardDescription>Gastos por categoria</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={categoryStats}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      label={(entry) => {
                        const total = categoryStats.reduce((sum, c) => sum + (c.amount || 0), 0);
                        const percentage = total > 0 ? ((entry.amount || 0) / total) * 100 : 0;
                        return `${entry.category}: ${percentage.toFixed(1)}%`;
                      }}
                    >
                      {categoryStats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Top Categorias</CardTitle>
                <CardDescription>Maiores gastos</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {categoryStats.slice(0, 8).map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${COLORS[idx % COLORS.length]}20` }}>
                          <span className="font-bold" style={{ color: COLORS[idx % COLORS.length] }}>{idx + 1}</span>
                        </div>
                        <span className="font-medium">{cat.category}</span>
                      </div>
                      <span className="font-bold tabular-nums">R$ {cat.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="comparison" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tendência de Crescimento</CardTitle>
              <CardDescription>Evolução do patrimônio</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={monthlyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
                  <Legend />
                  <Line type="monotone" dataKey="income" name="Receitas" stroke="#10b981" strokeWidth={3} dot={{ r: 6 }} />
                  <Line type="monotone" dataKey="expense" name="Despesas" stroke="#ef4444" strokeWidth={3} dot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
